import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  QUALIFYING_SCORE_GENDER_OPTIONS,
  QUALIFYING_SCORE_HEIGHT_OPTIONS,
} from "../../constants/data";
import {
  buildSavePayload,
  copyToForm,
  emptyQualifyingScoreForm,
  formatFullDateTime,
  fromDtoToForm,
  isValidEmail,
  splitRecipients,
  validateQualifyingScoreForm,
} from "../../utils/qualifyingScoreUtils";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
    gap: "12px",
    minWidth: "560px",
    "@media (max-width: 760px)": {
      gridTemplateColumns: "1fr",
      minWidth: "280px",
    },
  },
  full: { gridColumn: "1 / -1" },
  readOnlyRow: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "4px 0",
  },
  readOnlyLabel: { fontSize: "12px", color: "#667085" },
  readOnlyValue: { fontSize: "14px", color: "#101828" },
  recipientList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    padding: "8px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    minHeight: "44px",
    backgroundColor: "#fff",
  },
  recipientInputRow: {
    display: "flex",
    gap: "8px",
    marginTop: "8px",
  },
  audit: {
    color: "#667085",
    fontSize: "12px",
    marginTop: "8px",
  },
  actionsRow: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },
  rightActions: {
    display: "flex",
    gap: "8px",
  },
  errorChip: {
    backgroundColor: "#fef3f2",
    color: "#b42318",
    border: "1px solid #fda29b",
  },
});

const QualifyingScoreForm = ({
  selectedDto,
  copyFrom,
  governingBodyName,
  governingBodyId,
  messageTemplates,
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting,
}) => {
  const classes = useStyles();
  const isEdit = Boolean(selectedDto?.id);

  // Create, optionally prefilled from an existing score (Copy). Nothing is saved until Save.
  const initialValues = () => {
    if (isEdit) return fromDtoToForm(selectedDto);
    if (copyFrom) return copyToForm(copyFrom, governingBodyId);
    return emptyQualifyingScoreForm(governingBodyId);
  };
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [recipientDraft, setRecipientDraft] = useState("");

  useEffect(() => {
    setValues(initialValues());
    setErrors({});
    setRecipientDraft("");
    // initialValues reads exactly these inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDto, copyFrom, isEdit, governingBodyId]);

  const set = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
  };

  const handleAddRecipient = () => {
    const parts = splitRecipients(recipientDraft);
    if (parts.length === 0) return;
    const combined = [...values.emailRecipients, ...parts];
    const lower = new Set();
    const deduped = [];
    combined.forEach((r) => {
      const key = String(r).trim().toLowerCase();
      if (key && !lower.has(key)) {
        lower.add(key);
        deduped.push(r);
      }
    });
    set({ emailRecipients: deduped });
    setRecipientDraft("");
  };

  const handleRecipientPaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;
    if (/[\s,;]/.test(pasted)) {
      e.preventDefault();
      const parts = splitRecipients(pasted);
      if (parts.length) {
        const combined = [...values.emailRecipients, ...parts];
        const lower = new Set();
        const deduped = [];
        combined.forEach((r) => {
          const key = String(r).trim().toLowerCase();
          if (key && !lower.has(key)) {
            lower.add(key);
            deduped.push(r);
          }
        });
        set({ emailRecipients: deduped });
      }
    }
  };

  const handleRemoveRecipient = (email) => {
    set({
      emailRecipients: values.emailRecipients.filter(
        (r) => r.toLowerCase() !== email.toLowerCase(),
      ),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validateQualifyingScoreForm(values);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    const payload = buildSavePayload(values, selectedDto);
    onSubmit(payload);
  };

  // Templates this governing body may use. Keep the score's current choice selectable even
  // before the list arrives, or if it was deactivated and the list omits it.
  const templateOptions = useMemo(() => {
    const list = Array.isArray(messageTemplates) ? messageTemplates : [];
    const currentId = selectedDto?.messageTemplateId;
    if (currentId == null || list.some((t) => t.id === currentId)) return list;
    return [
      ...list,
      {
        id: currentId,
        description: selectedDto?.messageTemplateDescription,
        active: selectedDto?.messageTemplateActive !== false,
        localeIds: [],
      },
    ];
  }, [messageTemplates, selectedDto]);
  const selectedTemplate = templateOptions.find(
    (t) => String(t.id) === String(values.messageTemplateId),
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Box className={classes.grid}>
        <Box className={classes.readOnlyRow}>
          <Typography className={classes.readOnlyLabel}>Governing body</Typography>
          <Typography className={classes.readOnlyValue}>
            {governingBodyName || `GB ${governingBodyId}`}
          </Typography>
        </Box>

        {!isEdit && copyFrom?.id != null && (
          <Box className={classes.readOnlyRow}>
            <Typography className={classes.readOnlyLabel}>Copy of</Typography>
            <Typography className={classes.readOnlyValue}>
              #{copyFrom.id} — adjust anything, then Save to create a new score
              {copyFrom.messageTemplateActive === false &&
                ". Its template is inactive, so pick another"}
            </Typography>
          </Box>
        )}

        {isEdit && (
          <Box className={classes.readOnlyRow}>
            <Typography className={classes.readOnlyLabel}>ID</Typography>
            <Typography className={classes.readOnlyValue}>{selectedDto?.id}</Typography>
          </Box>
        )}

        <TextField
          label="Age group *"
          variant="outlined"
          size="small"
          value={values.ageGroup}
          onChange={(e) => set({ ageGroup: e.target.value })}
          error={Boolean(errors.ageGroup)}
          helperText={errors.ageGroup}
        />

        <FormControl variant="outlined" size="small" error={Boolean(errors.gender)}>
          <InputLabel>Gender *</InputLabel>
          <Select
            value={values.gender}
            onChange={(e) => set({ gender: e.target.value })}
            label="Gender *"
          >
            <MenuItem value="">
              <em>Select…</em>
            </MenuItem>
            {QUALIFYING_SCORE_GENDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
        </FormControl>

        <FormControl
          component="fieldset"
          error={Boolean(errors.eventHeight)}
          className={classes.full}
        >
          <Typography className={classes.readOnlyLabel}>Height *</Typography>
          <RadioGroup
            row
            value={String(values.eventHeight || "")}
            onChange={(e) => set({ eventHeight: Number(e.target.value) })}
          >
            {QUALIFYING_SCORE_HEIGHT_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={String(option.value)}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {errors.eventHeight && <FormHelperText>{errors.eventHeight}</FormHelperText>}
        </FormControl>

        <TextField
          label="Dive count *"
          variant="outlined"
          size="small"
          type="number"
          value={values.diveCount}
          onChange={(e) => set({ diveCount: e.target.value })}
          inputProps={{ min: 1 }}
          error={Boolean(errors.diveCount)}
          helperText={errors.diveCount}
        />

        <TextField
          label="Points *"
          variant="outlined"
          size="small"
          type="number"
          value={values.points}
          onChange={(e) => set({ points: e.target.value })}
          inputProps={{ min: 1, max: 999.99, step: "any" }}
          error={Boolean(errors.points)}
          helperText={errors.points || "1.00 – 999.99, rounded to 2 decimal places"}
        />

        <TextField
          label="From date *"
          variant="outlined"
          size="small"
          type="date"
          value={values.fromDate}
          onChange={(e) => set({ fromDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.fromDate)}
          helperText={errors.fromDate}
        />

        <TextField
          label="To date *"
          variant="outlined"
          size="small"
          type="date"
          value={values.toDate}
          onChange={(e) => set({ toDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.toDate)}
          helperText={errors.toDate}
        />

        <TextField
          label="Minimum DD"
          variant="outlined"
          size="small"
          type="number"
          value={values.minimumDd ?? ""}
          onChange={(e) => set({ minimumDd: e.target.value })}
          inputProps={{ min: 0, step: "0.1" }}
          error={Boolean(errors.minimumDd)}
          helperText={errors.minimumDd}
        />

        <TextField
          label="Attempts required"
          variant="outlined"
          size="small"
          type="number"
          value={values.attemptsRequired ?? 0}
          onChange={(e) => set({ attemptsRequired: e.target.value })}
          inputProps={{ min: 0 }}
          error={Boolean(errors.attemptsRequired)}
          helperText={errors.attemptsRequired}
        />

        <TextField
          label="Conference (id)"
          variant="outlined"
          size="small"
          type="number"
          value={values.conferenceId ?? ""}
          onChange={(e) => set({ conferenceId: e.target.value })}
          inputProps={{ min: 0 }}
          helperText="Typeahead deferred for v1 — paste the conference id."
        />

        <TextField
          label="Division"
          variant="outlined"
          size="small"
          value={values.divisionId ?? ""}
          onChange={(e) => set({ divisionId: e.target.value })}
        />

        <TextField
          label="Region"
          variant="outlined"
          size="small"
          value={values.regionId ?? ""}
          onChange={(e) => set({ regionId: e.target.value })}
        />

        <TextField
          id="qs-form-state"
          label="State"
          variant="outlined"
          size="small"
          value={values.state ?? ""}
          onChange={(e) => set({ state: e.target.value })}
          helperText="Must match the team address state exactly; blank = any state."
          // dive_qualifying_score.state is VARCHAR(3); a longer value fails on save.
          inputProps={{ maxLength: 3 }}
        />

        <FormControl
          variant="outlined"
          size="small"
          className={classes.full}
          error={Boolean(errors.messageTemplateId)}
        >
          <InputLabel>Message template *</InputLabel>
          <Select
            value={values.messageTemplateId}
            onChange={(e) => set({ messageTemplateId: e.target.value })}
            label="Message template *"
          >
            <MenuItem value="">
              <em>Select…</em>
            </MenuItem>
            {templateOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                disabled={
                  option.active === false &&
                  option.id !== selectedDto?.messageTemplateId
                }
              >
                {option.description || `Template ${option.id}`}
                {option.active === false ? " (inactive)" : ""}
                {option.localeIds?.length
                  ? ` — ${option.localeIds.join(", ")}`
                  : ""}
              </MenuItem>
            ))}
          </Select>
          {errors.messageTemplateId ? (
            <FormHelperText>{errors.messageTemplateId}</FormHelperText>
          ) : (
            <FormHelperText>
              {selectedTemplate?.active === false
                ? "This template is inactive: no email is sent for this score until you pick an active one."
                : "Sent in the diver's language when the template has it, otherwise a related language or en-US."}
            </FormHelperText>
          )}
        </FormControl>

        <Box className={classes.full}>
          <Typography className={classes.readOnlyLabel}>Email recipients</Typography>
          <Box className={classes.recipientList}>
            {values.emailRecipients.length === 0 && (
              <Typography variant="caption" style={{ color: "#98a2b3" }}>
                No recipients
              </Typography>
            )}
            {values.emailRecipients.map((email) => {
              const invalid = !isValidEmail(email);
              return (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => handleRemoveRecipient(email)}
                  className={invalid ? classes.errorChip : ""}
                />
              );
            })}
          </Box>
          <Box className={classes.recipientInputRow}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Type or paste emails (comma, semicolon, or newline-separated)"
              value={recipientDraft}
              onChange={(e) => setRecipientDraft(e.target.value)}
              onPaste={handleRecipientPaste}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddRecipient();
                }
              }}
              error={Boolean(errors.emailRecipients)}
              helperText={errors.emailRecipients}
            />
            <Button variant="outlined" onClick={handleAddRecipient}>
              Add
            </Button>
          </Box>
        </Box>

        {isEdit && (
          <Box className={classes.full}>
            <Typography className={classes.audit}>
              Created {formatFullDateTime(selectedDto?.createdAt) || "—"}
              {selectedDto?.createdBy != null ? ` by user ${selectedDto.createdBy}` : ""}
            </Typography>
            <Typography className={classes.audit}>
              Updated {formatFullDateTime(selectedDto?.updatedAt) || "—"}
              {selectedDto?.updatedBy != null ? ` by user ${selectedDto.updatedBy}` : ""}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.actionsRow}>
        {isEdit ? (
          <Button
            color="secondary"
            variant="outlined"
            onClick={onDelete}
            disabled={isSubmitting}
          >
            Delete
          </Button>
        ) : (
          <span />
        )}
        <Box className={classes.rightActions}>
          <Button onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </Box>
    </form>
  );
};

QualifyingScoreForm.propTypes = {
  selectedDto: PropTypes.object,
  /** A score to prefill the create form from (Copy); ignored when editing. */
  copyFrom: PropTypes.object,
  governingBodyName: PropTypes.string,
  governingBodyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  messageTemplates: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  isSubmitting: PropTypes.bool,
};

export default QualifyingScoreForm;
